import { startFirebaseCredential } from "@a2z/realtime/firebase";
import { useEffect, useRef, useSyncExternalStore } from "react";
import {
  requestFcmToken,
  registerFcmTokenWithBackend,
} from "@/lib/firebase/fcm";
import { getFirebaseAuth, getFirebaseMessaging } from "@/lib/firebase/config";
import { onMessage } from "firebase/messaging";
import { activateAdminFirebaseAuthSession, signInAdminFirebaseAuth, signOutAdminFirebaseAuth } from "@/lib/firebase/auth-session";

import { getPrivateSessionState, subscribePrivateSession } from "@/lib/api/private-session";

type FcmBootstrapProps = {
  adminId: string | undefined;
  firebaseToken?: string | null;
  /** A failed or stalled credential exchange; the shell shows it as degraded. */
  onCredentialError?: () => void;
};

export function FcmBootstrap({ adminId, firebaseToken, onCredentialError }: FcmBootstrapProps) {
  const registeredRef = useRef<string | null>(null);
  const lifetime = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);

  // Register/refresh FCM token on mount — request permission if not yet granted
  useEffect(() => {
    if (!adminId) return;
    if (typeof Notification === "undefined") return;
    if (registeredRef.current === adminId) return;

    let cancelled = false;
    void (async () => {
      // ponytail: never auto-prompt; NotificationPromptBanner owns the request on user click.
      if (Notification.permission !== "granted") return;

      const token = await requestFcmToken();
      if (cancelled || !token) return;
      await registerFcmTokenWithBackend(token);
      registeredRef.current = adminId;
    })();

    return () => {
      cancelled = true;
    };
  }, [adminId]);

  // Sign in to Firebase Auth for Firestore access (re-signs on token refresh)
  useEffect(() => {
    if (!adminId || lifetime.changing) {
      void signOutAdminFirebaseAuth().catch(() => undefined);
      return;
    }
    if (!firebaseToken) {
      const auth = getFirebaseAuth();
      if (!auth || auth.currentUser?.uid === adminId) return;
      let active = true;
      // Report after the listener's initial connecting state, without retrying /me.
      queueMicrotask(() => { if (active) onCredentialError?.(); });
      return () => { active = false; };
    }
    const generation = lifetime.generation;
    const isCurrent = () => !getPrivateSessionState().changing && getPrivateSessionState().generation === generation;
    activateAdminFirebaseAuthSession();
    return startFirebaseCredential({
      signIn: () => signInAdminFirebaseAuth(firebaseToken),
      isAuthenticated: () => getFirebaseAuth()?.currentUser?.uid === adminId,
      isCurrent, onError: onCredentialError,
    });
  }, [adminId, firebaseToken, lifetime.changing, lifetime.generation, onCredentialError]);

  // Credential replacement for the same actor must not tear down Firestore.
  useEffect(() => () => { void signOutAdminFirebaseAuth().catch(() => undefined); }, []);

  // Foreground messages are handled by useNotifications (Firestore listener).
  // We only need to consume the onMessage stream so Firebase doesn't warn.
  useEffect(() => {
    const messaging = getFirebaseMessaging();
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, () => {
      // No-op: in-app notification UI is driven by Firestore snapshots
    });

    return () => unsubscribe();
  }, []);

  return null;
}
